import authProviderV1 from '../v1/auth';
import { TokenType, UserContextEnum } from '../types';
import { LoginInputType } from '../../infra/startup/types';
import { ISessionService } from '../../services/session';
import ITenantHandlerService from '../../services/tenantHandler/ITenantHandlerService';
import { ILoginService } from '../../services/session/loginService';
import IAuthInterface from '../v1/auth/IAuthInterface';
import { ForceLoginResponseType } from '../../services/session/ISessionService';

/**
 * AuthProvider v2 - Delegates to v1 implementations for consistency
 * All v2 methods now delegate to v1 implementations to reduce duplication.
 */
export default function authProvider(
  loginInput?: LoginInputType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tokenTypeName: TokenType = TokenType.stratusAccessToken,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tenantId = ''
): AuthProviderV2Type {
  // Note: loginInput, tokenTypeName, and tenantId are kept for backwards compatibility
  // They are not used in the new delegation implementation
  // Cache for v1 instance to avoid multiple async calls
  let v1Instance: IAuthInterface | null = null;

  /**
   * Get the v1 auth provider instance (singleton)
   */
  const getV1Instance = (): IAuthInterface => {
    if (!v1Instance) {
      v1Instance = authProviderV1();
    }
    return v1Instance;
  };

  return {
    /**
     * Service setters are not needed in v2 as v1 uses dependency injection
     * This method is now a no-op
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _setSessionService: (_sessionService: ISessionService): void => {
      // No-op: v1 uses dependency injection
    },

    /**
     * Service setters are not needed in v2 as v1 uses dependency injection
     * This method is now a no-op
     */
    _setTenantHandlerService: (
      _tenantHandlerService: ITenantHandlerService
    ): void => {
      // Satisfy linter by using the parameter
      void _tenantHandlerService;
      // No-op: v1 uses dependency injection
    },

    /**
     * Service setters are not needed in v2 as v1 uses dependency injection
     * This method is now a no-op
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _setLoginService: (_loginService: ILoginService): void => {
      // No-op: v1 uses dependency injection
    },

    /**
     * Get access token - delegates to v1 implementation
     */
    getAccessToken: (forceRefresh?: boolean): Promise<string> => {
      const v1 = getV1Instance();
      return v1.getAccessToken(forceRefresh);
    },

    /**
     * Get ID token - delegates to v1 implementation
     */
    getIDToken: async (): Promise<string> => {
      const v1 = getV1Instance();
      return v1.getIDToken();
    },

    /**
     * Handle token exchange - delegates to v1 implementation
     */
    onTokenExchangeRequired: (
      userType: UserContextEnum = UserContextEnum.organization,
      tenantId?: string
    ): Promise<void> => {
      const v1 = getV1Instance();
      return v1.onTokenExchangeRequired(userType, tenantId);
    },

    /**
     * Force login - delegates to v1 implementation
     */
    forceLogin: (): Promise<ForceLoginResponseType> => {
      const v1 = getV1Instance();
      return v1.forceLogin();
    },

    /**
     * Get decoded user JWT payload - delegates to v1 implementation
     */
    getDecodedUserStratusJWTPayload: (): unknown => {
      const v1 = getV1Instance();
      // v1 accepts TokenType parameter, default to stratusAccessToken to match v2 behavior
      return v1.getDecodedUserStratusJWTPayload(TokenType.stratusAccessToken);
    },

    /**
     * Create orgless auth provider - delegates to v1 implementation
     */
    createOrglessAuthProvider: (tenantId = ''): AuthProviderV2Type => {
      // Delegate to v1 factory method and return the result
      const v1 = getV1Instance();
      return v1.createOrglessAuthProvider(tenantId) as AuthProviderV2Type;
    },

    /**
     * Create orged auth provider - delegates to v1 implementation
     */
    createOrgedAuthProvider: (tenantId = ''): AuthProviderV2Type => {
      // Delegate to v1 factory method and return the result
      const v1 = getV1Instance();
      return v1.createOrgedAuthProvider(tenantId) as AuthProviderV2Type;
    },

    /**
     * Create auth provider by user context - delegates to v1 implementation
     */
    createAuthProviderByUserContextEnum: (
      userContext: UserContextEnum
    ): AuthProviderV2Type => {
      // Delegate to v1 factory method and return the result
      const v1 = getV1Instance();
      return v1.createAuthProviderByUserContextEnum(
        userContext
      ) as AuthProviderV2Type;
    }
  };
}

export type AuthProviderV2Type = {
  _setSessionService: (sessionService: ISessionService) => void;
  _setTenantHandlerService: (
    tenantHandlerService: ITenantHandlerService
  ) => void;
  _setLoginService: (loginService: ILoginService) => void;
  getAccessToken: (forceRefresh?: boolean) => Promise<string>;
  getIDToken: () => Promise<string>;
  onTokenExchangeRequired: (
    userType?: UserContextEnum,
    tenantId?: string
  ) => Promise<void>;
  forceLogin: () => Promise<ForceLoginResponseType>;
  getDecodedUserStratusJWTPayload: () => unknown;
  createOrglessAuthProvider: (tenantId?: string) => AuthProviderV2Type;
  createOrgedAuthProvider: (tenantId?: string) => AuthProviderV2Type;
  createAuthProviderByUserContextEnum: (
    userContext: UserContextEnum
  ) => AuthProviderV2Type;
};
